import { emailQuery } from "@common/api"
import type { EmailDetailPublic } from "@common/client"
import { Badge } from "@common/components/ui/badge"
import { Separator } from "@common/components/ui/separator"
import { useEscape } from "@common/hooks/use-escape"
import { formatDate } from "@common/lib/date-utils"
import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, Outlet, useNavigate } from "@tanstack/react-router"
import { CalendarDays, Mail } from "lucide-react"
import { DetailPane, DetailPanelFields } from "@/components/layout/detail-panel"
import { MetaItem, MetaList } from "@/components/page/meta"
import { PaneTitle } from "@/components/page/pane"
import { DocumentChipView } from "@/routes/_layout/documents/-components/document-chip-view"
import { EmailActionsMenu } from "../-components/email-actions-menu"

export const Route = createFileRoute("/_layout/quoting_/emails/_list/$emailId")(
  {
    staticData: { detailSidebar: true },
    loader: ({ context: { queryClient }, params: { emailId } }) =>
      queryClient.ensureQueryData(emailQuery({ email_id: emailId })),
    component: EmailPane,
  },
)

function EmailPane(): React.ReactElement {
  const { emailId } = Route.useParams()
  const { data: email } = useSuspenseQuery(emailQuery({ email_id: emailId }))
  const navigate = useNavigate()
  const close = () => navigate({ to: "/quoting/emails", search: true })
  useEscape(close)

  return (
    <DetailPane
      title={email.subject}
      heading={<Heading email={email} />}
      onClose={close}
    >
      <DetailPanelFields>
        <PaneTitle title={email.subject}>
          <Meta email={email} />
        </PaneTitle>
        <Body email={email} />
        <Documents email={email} />
      </DetailPanelFields>
      <Outlet />
    </DetailPane>
  )
}

function Heading({ email }: { email: EmailDetailPublic }): React.ReactElement {
  return (
    <>
      <span className="text-small-regular text-muted-foreground">Email</span>
      <div className="ml-auto">
        <EmailActionsMenu email={email} />
      </div>
    </>
  )
}

function Meta({ email }: { email: EmailDetailPublic }): React.ReactElement {
  return (
    <MetaList>
      <MetaItem icon={Mail}>{email.from_address}</MetaItem>
      <MetaItem icon={CalendarDays}>{formatDate(email.created_at)}</MetaItem>
      {email.archived_at && <Badge variant="secondary">Archived</Badge>}
    </MetaList>
  )
}

function Body({ email }: { email: EmailDetailPublic }): React.ReactElement {
  return (
    <div className="flex flex-col gap-4">
      <p className="whitespace-pre-wrap text-body-medium">
        {email.stripped_text ?? email.body_plain}
      </p>
      {email.stripped_signature && (
        <p className="whitespace-pre-wrap text-small-medium text-muted-foreground">
          {email.stripped_signature}
        </p>
      )}
    </div>
  )
}

function Documents({
  email,
}: {
  email: EmailDetailPublic
}): React.ReactElement | null {
  const documents = email.visible_documents ?? []
  if (!documents.length) {
    return null
  }

  return (
    <>
      <Separator className="my-4" />
      <DocumentChipView documents={documents} />
    </>
  )
}
